import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Button, Space, Typography } from "antd";
import { Clock, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import Navbar from "Components/Navbar/Navbar";
import Footer from "Components/Footer/Footer";
import PageLoader from "Components/PageLoader/PageLoader";
import AccountDetailView from "Components/AccountDetailView/AccountDetailView";
import WhileWeReview from "./_blocks/WhileWeReview";
import { getServiceAccount } from "services/serviceAccount.service";
import { renderErrorNotifications } from "helpers/error.helpers";

const { Title, Text } = Typography;

const STATUS_CONFIG = {
  PENDING: {
    icon: Clock,
    iconClass: "bg-amber-100 text-amber-600",
    heading: "Application under review",
    description:
      "We're currently reviewing your application. This usually takes 2–3 business days. We'll notify you via email once a decision is made.",
  },
  VERIFIED: {
    icon: CheckCircle2,
    iconClass: "bg-emerald-100 text-emerald-600",
    heading: "Application approved",
    description:
      "Congratulations! Your application has been approved. You now have full access to the community portal.",
  },
  VERIFIED_INACTIVE: {
    icon: HelpCircle,
    iconClass: "bg-orange-100 text-orange-600",
    heading: "Account suspended",
    description:
      "Your account has been temporarily deactivated by our admin team. This may be due to a policy violation or a pending review. Please get in touch with us and share your Account ID to resolve this.",
  },
  REJECTED: {
    icon: XCircle,
    iconClass: "bg-red-100 text-red-600",
    heading: "Application not approved",
    description:
      "Unfortunately, your application was not approved at this time. Please see the details below or contact support.",
  },
};

function ApplicationStatus() {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("account_id");
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, errors } = await getServiceAccount(accountId);
      if (errors?.length) renderErrorNotifications(errors);
      else setAccount(data);
      setLoading(false);
    }
    load();
  }, [accountId]);

  if (loading) return <PageLoader />;

  const status = account?.verification_status ?? "PENDING";
  const isVerifiedButInactive =
    status === "VERIFIED" && account?.is_active === false;
  const cfgKey = isVerifiedButInactive ? "VERIFIED_INACTIVE" : status;
  const cfg = STATUS_CONFIG[cfgKey] ?? STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;

  const displayAccountId = accountId || account?.account_id;

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl space-y-10 animate-in">
            {/* ── Status Hero Card ─────────────────────────────────────── */}
            <div className="rounded-3xl border border-border/50 bg-card p-12 text-center shadow-card space-y-6">
              <div className="flex justify-center">
                <div
                  className={`flex h-20 w-20 items-center justify-center rounded-full ${cfg.iconClass}`}
                >
                  <Icon className="h-10 w-10" />
                </div>
              </div>

              <div className="space-y-2">
                <h1 className="text-statement text-3xl md:text-4xl">
                  {cfg.heading}
                </h1>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {status === "REJECTED" && account?.reason_for_rejection
                    ? account.reason_for_rejection
                    : cfg.description}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                {status === "VERIFIED" && !isVerifiedButInactive && (
                  <Button
                    size="large"
                    className="px-8"
                    onClick={() => navigate("/login")}
                  >
                    Go to Dashboard
                  </Button>
                )}
                {isVerifiedButInactive && displayAccountId && (
                  <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 max-w-md mx-auto text-sm text-orange-800 space-y-1">
                    <p className="font-semibold">Contact our support team</p>
                    <p>
                      Share your <strong>Account ID</strong> when reaching out:
                    </p>
                    <Typography.Text
                      copyable={{ text: displayAccountId }}
                      style={{ fontFamily: "monospace",color: "inherit", fontSize: 13 }}
                    >
                      {displayAccountId}
                    </Typography.Text>
                  </div>
                )}
              </div>
            </div>

            {/* ── While we review ──────────────────────────────────────── */}
            {status === "PENDING" && <WhileWeReview />}

            {/* ── Full Account Details ─────────────────────────────────── */}
            {account && (
              <Space direction="vertical" size={24} style={{ width: "100%" }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    Application Details
                  </Title>
                  <Text type="secondary">
                    A full summary of your submitted application.
                  </Text>
                </div>
                <AccountDetailView account={account} />
              </Space>
            )}

            {/* ── Contact Note ─────────────────────────────────────────── */}
            {displayAccountId && (
              <Alert
                type="warning"
                showIcon
                message="Taking too long?"
                description={
                  <span>
                    If you feel your application is taking unusually long,
                    please contact our admin team and share your{" "}
                    <strong>Account ID</strong>:{" "}
                    <Typography.Text
                      copyable={{ text: displayAccountId }}
                      style={{ fontFamily: "monospace", fontSize: 13 }}
                    >
                      {displayAccountId}
                    </Typography.Text>
                  </span>
                }
              />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default ApplicationStatus;
